import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { smartUpload } from "@/lib/upload";
import { UploadProgress } from "@/components/UploadProgress";
import { ShareDialog } from "@/components/ShareDialog";
import { FolderBreadcrumb, lastSegment, parentPath } from "@/components/FolderBreadcrumb";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDateTime } from "@/components/badges";
import { useTranslation } from "react-i18next";
import { Archive, Download, Folder, FolderDown, FolderPlus, FolderUp, History, Loader2, MoveRight, Pencil, Share2, Trash2, Upload } from "lucide-react";
import { fmtSize, saveBlob } from "./drive/utils";
import { VersionsDialog } from "./drive/VersionsDialog";
import { RenameFileDialog } from "./drive/RenameFileDialog";
import { RenameFolderDialog } from "./drive/RenameFolderDialog";
import { NewFolderDialog } from "./drive/NewFolderDialog";
import { MoveDialog } from "./drive/MoveDialog";
import { useConfirm } from "@/components/ConfirmDialog";
import { FolderPickerDialog } from "@/components/FolderPickerDialog";

const DRAG_FILE_TYPE = "application/x-pruva-file";
const isInternalDrag = (e) => e.dataTransfer.types.includes(DRAG_FILE_TYPE);

export default function DriveExplorer({ scope, ownerId = null, ownerLabel = "" }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { askConfirm, confirmDialog } = useConfirm();
  const [folder, setFolder] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [selectedFolders, setSelectedFolders] = useState(() => new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ folder: "", version: "", note: "" });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [renameFile, setRenameFile] = useState(null);
  const [renameFolder, setRenameFolder] = useState(null);
  const [moveDoc, setMoveDoc] = useState(null);
  const [folderOpen, setFolderOpen] = useState(false);
  const [versionsDoc, setVersionsDoc] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [zipping, setZipping] = useState(false);
  const fileRef = useRef(null);
  const dirRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);
  const [dropFolder, setDropFolder] = useState(null);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);

  const queryKey = ["drive", scope, ownerId || "me"];
  const { data: lib, isLoading } = useQuery({
    queryKey,
    queryFn: async () => (await api.get("/drive/files", { params: { scope, ...(ownerId ? { owner_id: ownerId } : {}) } })).data,
  });

  const allFiles = useMemo(() => (lib?.files || []).filter((f) => f.is_latest !== false), [lib]);
  const realFolders = useMemo(() => (lib?.folders || []).map((f) => f.name), [lib]);
  const folderTree = useMemo(() => {
    const names = new Set(realFolders);
    if (allFiles.some((f) => !f.folder)) names.add("General");
    return [...names].sort();
  }, [realFolders, allFiles]);

  const cur = folder;
  useEffect(() => { setSelectedFolders(new Set()); }, [cur]);
  const childFolders = useMemo(() => folderTree.filter((n) => parentPath(n) === cur), [folderTree, cur]);
  const files = useMemo(() => allFiles.filter((f) => (f.folder || "General") === cur), [allFiles, cur]);
  const subtreeCount = (name) => allFiles.filter((f) => { const ff = f.folder || "General"; return ff === name || ff.startsWith(name + "/"); }).length;
  const hasContents = (name) => subtreeCount(name) > 0 || folderTree.some((n) => n !== name && n.startsWith(name + "/"));

  const invalidate = () => qc.invalidateQueries({ queryKey });

  const pickFiles = (list, isFolder) => {
    const arr = Array.from(list || []);
    arr.forEach((f) => {
      const rel = f.webkitRelativePath || "";
      f._relDir = isFolder && rel.includes("/") ? rel.split("/").slice(0, -1).join("/") : "";
    });
    setUploadFiles(arr);
    setUploadForm({ folder: cur, version: "", note: "" });
    setUploadOpen(true);
  };

  const traverseEntry = async (entry, prefix = "") => {
    if (entry.isFile) {
      const file = await new Promise((res, rej) => entry.file(res, rej));
      file._relDir = prefix;
      return [file];
    }
    if (entry.isDirectory) {
      const reader = entry.createReader();
      let entries = [];
      let batch;
      do {
        batch = await new Promise((res, rej) => reader.readEntries(res, rej));
        entries = entries.concat(batch);
      } while (batch.length > 0);
      const sub = await Promise.all(entries.map((e) => traverseEntry(e, prefix ? `${prefix}/${entry.name}` : entry.name)));
      return sub.flat();
    }
    return [];
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    const items = Array.from(e.dataTransfer.items || []);
    const entries = items.map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null)).filter(Boolean);
    let files = [];
    if (entries.length) {
      const nested = await Promise.all(entries.map((en) => traverseEntry(en)));
      files = nested.flat();
    } else {
      files = Array.from(e.dataTransfer.files || []);
      files.forEach((f) => { f._relDir = ""; });
    }
    if (!files.length) return;
    setUploadFiles(files);
    setUploadForm({ folder: cur, version: "", note: "" });
    setUploadOpen(true);
  };

  const uploadMut = useMutation({
    mutationFn: async () => {
      const total = uploadFiles.reduce((a, f) => a + f.size, 0) || 1;
      let doneBytes = 0;
      const results = [];
      for (const f of uploadFiles) {
        const folderPath = [uploadForm.folder, f._relDir].filter(Boolean).join("/");
        const r = await smartUpload(
          f,
          { scope, owner_id: ownerId || undefined, folder: folderPath, version: uploadFiles.length === 1 ? uploadForm.version : "", note: uploadForm.note },
          (p) => setUploadProgress({ ...p, percent: Math.round(((doneBytes + (p.percent / 100) * f.size) / total) * 100), loaded: doneBytes + p.loaded, total }),
          "/drive/uploads"
        );
        if (!r) throw new Error("Direct S3 storage is unavailable");
        doneBytes += f.size;
        results.push(r);
      }
      return results;
    },
    onSuccess: (rs) => {
      toast.success(rs.length > 1 ? t("driveExplorer.uploadedCountToast", { count: rs.length }) : t("driveExplorer.uploadedToast") + (rs[0]?.version ? ` as ${rs[0].version}` : ""));
      setUploadOpen(false);
      setUploadProgress(null);
      setUploadFiles([]);
      setUploadForm({ folder: "", version: "", note: "" });
      invalidate();
    },
    onError: (e) => {
      toast.error(fmtErr(e));
      setUploadProgress(null);
    },
  });

  const delFolderMut = useMutation({
    mutationFn: async (name) => api.delete("/drive/folders", { params: { scope, name, ...(ownerId ? { owner_id: ownerId } : {}) } }),
    onSuccess: (_d, name) => {
      toast.success(t("driveExplorer.folderDeletedToast"));
      if (cur === name || cur.startsWith(name + "/")) setFolder(parentPath(name));
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => api.delete(`/drive/files/${id}`),
    onSuccess: () => {
      toast.success(t("driveExplorer.fileDeletedToast"));
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const moveMut = useMutation({
    mutationFn: async ({ id, folder: target }) => api.put(`/drive/files/${id}/move`, { folder: target }),
    onSuccess: () => { toast.success(t("driveExplorer.fileMovedToast")); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const selectedWritableIds = () => [...selected].filter((id) => { const f = allFiles.find((x) => x.id === id); return f && canModify(f); });

  const bulkMoveMut = useMutation({
    mutationFn: async (folder) => {
      const ids = selectedWritableIds();
      for (const id of ids) await api.put(`/drive/files/${id}/move`, { folder });
      return ids.length;
    },
    onSuccess: (n) => { toast.success(t("driveExplorer.movedSelectedToast", { count: n })); setSelected(new Set()); setBulkMoveOpen(false); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const bulkDeleteMut = useMutation({
    mutationFn: async () => {
      const ids = selectedWritableIds();
      for (const id of ids) await api.delete(`/drive/files/${id}`);
      return ids.length;
    },
    onSuccess: (n) => { toast.success(t("driveExplorer.deletedSelectedToast", { count: n })); setSelected(new Set()); invalidate(); },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const download = async (f) => {
    try {
      const res = await api.get(`/drive/files/${f.id}/download`, { responseType: "blob" });
      await saveBlob(res, f.filename);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleFolderSelect = (name) => {
    setSelectedFolders((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const zipIds = async (ids, name) => {
    setZipping(true);
    try {
      const res = await api.post("/drive/zip", { file_ids: ids }, { responseType: "blob" });
      await saveBlob(res, name);
      toast.success(t("driveExplorer.zippedToast", { count: ids.length }));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setZipping(false);
    }
  };

  const canModify = (f) => user?.is_super_admin || user?.is_admin || scope === "personal" || f.uploaded_by === user?.id;
  const rootLabel = ownerLabel || (scope === "shared" ? t("drive.tabShared") : t("drive.tabMy"));
  const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");

  return (
    <div
      className="relative space-y-4"
      data-testid={`drive-explorer-${scope}`}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={(e) => { e.preventDefault(); if (isInternalDrag(e)) return; dragCounter.current += 1; setDragging(true); }}
      onDragLeave={(e) => { e.preventDefault(); if (isInternalDrag(e)) return; dragCounter.current -= 1; if (dragCounter.current <= 0) { dragCounter.current = 0; setDragging(false); } }}
      onDrop={(e) => { if (isInternalDrag(e)) { e.preventDefault(); setDropFolder(null); return; } handleDrop(e); }}
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-sky-400 bg-sky-50/80" data-testid="drive-dropzone-overlay">
          <div className="flex items-center gap-2 text-sm font-semibold text-sky-700">
            <Upload className="h-5 w-5" /> {t("driveExplorer.dropHere")}
          </div>
        </div>
      )}
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <FolderBreadcrumb root={rootLabel} path={cur} onNavigate={setFolder} testidPrefix="drive" />
            <div className="ml-auto flex gap-2">
              {selected.size > 0 && (
                <Button size="sm" variant="outline" onClick={() => zipIds([...selected], "pruva-drive.zip")} disabled={zipping} data-testid="drive-zip-selected">
                  <Archive className="mr-1.5 h-4 w-4" /> {t("driveExplorer.zipSelected", { count: selected.size })}
                </Button>
              )}
              {selected.size > 0 && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setBulkMoveOpen(true)} data-testid="drive-move-selected">
                    <MoveRight className="mr-1.5 h-4 w-4" /> {t("driveExplorer.moveSelected", { count: selected.size })}
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700" onClick={() => askConfirm(t("driveExplorer.deleteSelectedConfirm", { count: selected.size }), () => bulkDeleteMut.mutate())} data-testid="drive-delete-selected">
                    <Trash2 className="mr-1.5 h-4 w-4" /> {t("driveExplorer.deleteSelected", { count: selected.size })}
                  </Button>
                </>
              )}
              {cur && (
                <Button size="sm" variant="outline" onClick={() => zipIds(allFiles.filter((f) => { const ff = f.folder || "General"; return ff === cur || ff.startsWith(cur + "/"); }).map((f) => f.id), `${scope}-${lastSegment(cur)}.zip`)} disabled={zipping} data-testid="drive-zip-folder">
                  <FolderDown className="mr-1.5 h-4 w-4" /> {t("driveExplorer.zipFolder")}
                </Button>
              )}
              {cur && (
                <Button size="sm" variant="outline" onClick={() => setShareTarget({ type: "folder", scope, ownerId, folder: cur })} data-testid="drive-share-folder">
                  <Share2 className="mr-1.5 h-4 w-4" /> {t("driveExplorer.shareFolder")}
                </Button>
              )}
              {selectedFolders.size > 0 && (
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => setShareTarget({ type: "folders", scope, ownerId, folders: [...selectedFolders] })} data-testid="drive-share-selected-folders">
                  <Share2 className="mr-1.5 h-4 w-4" /> {t("driveExplorer.shareSelected", { count: selectedFolders.size })}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => setFolderOpen(true)} data-testid="drive-new-folder">
                <FolderPlus className="mr-1.5 h-4 w-4" /> {t("driveExplorer.newFolder")}
              </Button>
              <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => { setUploadFiles([]); setUploadForm({ folder: cur, version: "", note: "" }); setUploadOpen(true); }} data-testid="drive-upload-button">
                <Upload className="mr-1.5 h-4 w-4" /> {t("driveExplorer.uploadFile")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => dirRef.current?.click()} data-testid="drive-upload-folder-button">
                <FolderUp className="mr-1.5 h-4 w-4" /> {t("driveExplorer.uploadFolder")}
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>{t("common.name")}</TableHead>
                  <TableHead>{t("common.version")}</TableHead>
                  <TableHead>{t("common.size")}</TableHead>
                  <TableHead>{t("common.uploaded")}</TableHead>
                  <TableHead className="text-right">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500">{t("common.loading")}</TableCell></TableRow>}
                {!isLoading && childFolders.length === 0 && files.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500" data-testid="drive-empty">{t("driveExplorer.empty")}</TableCell></TableRow>
                )}
                {childFolders.map((name) => {
                  const full = hasContents(name);
                  const isReal = realFolders.includes(name);
                  return (
                    <TableRow
                      key={name}
                      className={`cursor-pointer hover:bg-slate-50 ${dropFolder === name ? "bg-sky-50 ring-1 ring-inset ring-sky-300" : ""}`}
                      onClick={() => setFolder(name)}
                      onDragOver={(e) => { if (isInternalDrag(e)) { e.preventDefault(); e.stopPropagation(); setDropFolder(name); } }}
                      onDragLeave={() => setDropFolder((d) => (d === name ? null : d))}
                      onDrop={(e) => { if (!isInternalDrag(e)) return; e.preventDefault(); e.stopPropagation(); setDropFolder(null); const id = e.dataTransfer.getData(DRAG_FILE_TYPE); const src = allFiles.find((x) => x.id === id); if (!src || !canModify(src) || (src.folder || "General") === name) return; moveMut.mutate({ id, folder: name }); }}
                      data-testid={`drive-folder-row-${slug(name)}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedFolders.has(name)} onCheckedChange={() => toggleFolderSelect(name)} data-testid={`drive-folder-select-${slug(name)}`} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium text-slate-900">
                          <Folder className="h-4 w-4 text-amber-500" /> {lastSegment(name)}
                          <span className="font-mono text-[10px] text-slate-400">{t("common.filesCount", { count: subtreeCount(name) })}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-400">—</TableCell>
                      <TableCell className="font-mono text-xs text-slate-400">—</TableCell>
                      <TableCell className="text-xs text-slate-400">{t("badges.folder")}</TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        {isReal && (
                          <>
                            <Button variant="ghost" size="icon" title={t("common.renameFolder")} onClick={() => setRenameFolder(name)} data-testid={`drive-folder-rename-${slug(name)}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={full ? t("driveExplorer.folderNotEmptyT") : t("driveExplorer.deleteEmptyFolderT")}
                              disabled={full || delFolderMut.isPending}
                              className="text-rose-600 hover:text-rose-700 disabled:opacity-40"
                              onClick={() => askConfirm(t("driveExplorer.deleteFolderConfirm", { name: lastSegment(name) }), () => delFolderMut.mutate(name))}
                              data-testid={`drive-folder-delete-${slug(name)}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {files.map((f) => (
                  <TableRow key={f.id} draggable={canModify(f)} onDragStart={(e) => { e.dataTransfer.setData(DRAG_FILE_TYPE, f.id); e.dataTransfer.effectAllowed = "move"; }} data-testid={`drive-file-row-${f.id}`}>
                    <TableCell>
                      <Checkbox checked={selected.has(f.id)} onCheckedChange={() => toggleSelect(f.id)} data-testid={`drive-select-${f.id}`} />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{f.filename}</div>
                      {f.note && <div className="text-xs text-slate-500">{f.note}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs font-semibold">{f.version}</TableCell>
                    <TableCell className="font-mono text-xs">{fmtSize(f.size)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{f.uploaded_by_email} · {fmtDateTime(f.uploaded_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" title={t("library.downloadT")} onClick={() => download(f)} data-testid={`drive-download-${f.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title={t("driveExplorer.shareT")} onClick={() => setShareTarget({ type: "file", file: f })} data-testid={`drive-share-${f.id}`}>
                          <Share2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title={t("driveExplorer.versionsT")} onClick={() => setVersionsDoc(f)} data-testid={`drive-versions-${f.id}`}>
                          <History className="h-4 w-4" />
                        </Button>
                        {canModify(f) && (
                          <>
                            <Button variant="ghost" size="icon" title={t("common.renameFile")} onClick={() => setRenameFile(f)} data-testid={`drive-rename-${f.id}`}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" title={t("library.moveT")} onClick={() => setMoveDoc(f)} data-testid={`drive-move-${f.id}`}>
                              <MoveRight className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" title={t("common.delete")} className="text-rose-600" onClick={() => askConfirm(t("sharing.deleteConfirm", { filename: f.filename }), () => deleteMut.mutate(f.id))} data-testid={`drive-delete-${f.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ShareDialog target={shareTarget} onClose={() => setShareTarget(null)} onChanged={() => qc.invalidateQueries({ queryKey: ["drive-shares"] })} />

      <input ref={dirRef} type="file" className="hidden" {...{ webkitdirectory: "" }} onChange={(e) => { pickFiles(e.target.files, true); e.target.value = ""; }} data-testid="drive-dir-input" />

      <RenameFileDialog file={renameFile} queryKey={queryKey} onClose={() => setRenameFile(null)} />
      <RenameFolderDialog folder={renameFolder} scope={scope} ownerId={ownerId} cur={cur} queryKey={queryKey} onNavigate={setFolder} onClose={() => setRenameFolder(null)} />
      <MoveDialog doc={moveDoc} folders={folderTree} queryKey={queryKey} onClose={() => setMoveDoc(null)} />
      <FolderPickerDialog
        open={bulkMoveOpen}
        title={t("driveExplorer.moveSelected", { count: selected.size })}
        folders={folderTree}
        onSubmit={(target) => bulkMoveMut.mutate(target)}
        onClose={() => setBulkMoveOpen(false)}
        isPending={bulkMoveMut.isPending}
        testidPrefix="drive-bulk-move"
      />
      <VersionsDialog doc={versionsDoc} onClose={() => setVersionsDoc(null)} onDownload={download} />
      <NewFolderDialog open={folderOpen} onOpenChange={setFolderOpen} cur={cur} scope={scope} ownerId={ownerId} queryKey={queryKey} />

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent data-testid="drive-upload-dialog">
          <DialogHeader><DialogTitle>{t("driveExplorer.uploadFile")}{ownerLabel ? ` — ${ownerLabel}` : ""}{cur ? ` / ${cur}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("common.file")}</Label>
              <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => { pickFiles(e.target.files, false); e.target.value = ""; }} data-testid="drive-file-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="drive-choose-file">
                {uploadFiles.length > 0 ? t("sharing.filesSelected", { count: uploadFiles.length }) : t("driveExplorer.chooseFile")}
              </Button>
              {uploadFiles.length > 0 && (
                <ul className="max-h-32 space-y-0.5 overflow-y-auto text-xs text-slate-600">
                  {uploadFiles.slice(0, 20).map((f, i) => <li key={`${f._relDir || ""}/${f.name}-${i}`} className="font-mono">{(f._relDir ? f._relDir + "/" : "") + f.name} ({fmtSize(f.size)})</li>)}
                  {uploadFiles.length > 20 && <li>{t("sharing.andMore", { count: uploadFiles.length - 20 })}</li>}
                </ul>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("common.folder")}</Label>
                <Input list="drive-folder-suggestions" value={uploadForm.folder} onChange={(e) => setUploadForm({ ...uploadForm, folder: e.target.value })} placeholder="General" data-testid="drive-folder-input" />
                <datalist id="drive-folder-suggestions">
                  {folderTree.map((n) => <option key={n} value={n} />)}
                </datalist>
              </div>
              <div className="space-y-1.5">
                <Label>{t("driveExplorer.versionAutoL")}</Label>
                <Input value={uploadForm.version} onChange={(e) => setUploadForm({ ...uploadForm, version: e.target.value })} className="font-mono" placeholder="auto" data-testid="drive-version-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.noteOptional")}</Label>
              <Input value={uploadForm.note} onChange={(e) => setUploadForm({ ...uploadForm, note: e.target.value })} data-testid="drive-note-input" />
            </div>
          </div>
          {uploadProgress && <UploadProgress progress={uploadProgress} />}
          <DialogFooter>
            <Button onClick={() => uploadMut.mutate()} disabled={uploadFiles.length === 0 || uploadMut.isPending} data-testid="drive-upload-submit">
              {uploadMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {uploadMut.isPending ? t("common.uploading") : t("common.upload")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}
